import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { IncMoveCounter, SetFinished, SetGameProgress } from '../../store/puzzle-game.actions';
import { Button } from '../../shared-module/buttons/button.def';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';
import { HashiHelpComponent } from '../help/hashi-help.component';
import { HashiWinScreenComponent } from '../win-screen/hashi-win-screen.component';
import { HashiIsland } from '../interfaces/hashi-island';
import { HashiBridgeKey } from '../interfaces/hashi-bridge-key';
import { HashiPuzzleConfig } from '../interfaces/hashi-puzzle-config';
import { parseHashiPuzzleConfig } from '../config/hashi-puzzle-config.adapter';
import {
  HashiBridgesMap,
  computeNeighborPairs,
  computeProgress,
  createsCrossing,
  currentDegree,
  getOrientation,
  isSolved,
  isVisiblePair,
  lineEndpointsH,
  lineEndpointsV,
  normalizeKey,
  serializeKey,
} from '../geometry/hashi-geometry';
import { HashiPointerController, HashiPointerIntent } from '../interaction/hashi-pointer-controller';
import { HashiSizing, computeHashiSizing } from '../sizing/hashi-responsive-sizing';
import { InitHashiGame, PersistHashiState } from '../store/hashi-game.actions';
import { HashiGameModel } from '../store/hashi-game.model';
import { HashiGameState } from '../store/hashi-game.state';

@Component({
  selector: 'lib-app-hashi-board',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hashi-board.component.html',
  styleUrls: ['./hashi-board.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HashiBoardComponent implements OnInit, OnDestroy {
  islands: HashiIsland[] = [];
  bridges: HashiBridgesMap = {};
  bridgeKeys: HashiBridgeKey[] = [];

  cellSize = 60;
  islandRadius = 18;
  bridgeStroke = 6;
  bridgeOffset = 7;
  hitStrokeWidth = 28;
  islandFontSize = 16;
  islandTapExtra = 8;
  boardPadding = 28;

  widthPX = 600;
  heightPX = 400;

  showErrorsImmediately = false;

  private readonly pointer = new HashiPointerController();
  private puzzleID = '';
  private subs: Subscription[] = [];
  private checkFlash = false;
  private checkErrorNodeIds = new Set<string>();
  private checkTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(
    public framework: PuzzleFrameworkService,
    private store: Store,
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private winDialogService: PuzzleWinDialogService,
    private helpDialogService: PuzzleHelpDialogService,
    // Injecting settings modal service ensures its constructor runs and hooks SettingsMenu
    private _settingsModalService: PuzzleSettingsModalService
  ) {
    this.subs.push(
      this.winDialogService.winDialogRegistered.subscribe(() => {
        this.winDialogService.injectComponent(HashiWinScreenComponent, {});
        this.winDialogService.showButtons([
          PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME,
          PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME,
        ]);
      })
    );
    this.subs.push(
      this.helpDialogService.helpDialogRegistered.subscribe(() => {
        this.helpDialogService.injectHelpComponent(HashiHelpComponent, { translationKey: 'HASHI' });
      })
    );
  }

  ngOnInit(): void {
    this.applySizing();

    const parsed = parseHashiPuzzleConfig(this.framework.puzzleConfig as HashiPuzzleConfig);
    this.islands = parsed.islands;
    this.puzzleID = parsed.puzzleID;

    this.recomputeBoardDimensions();
    this.bridgeKeys = computeNeighborPairs(this.islands);
    this.bridges = this.makeEmptyBridges();

    this.store.dispatch(new SetPuzzleID({ puzzleID: this.puzzleID }));
    this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.HashiGame, 0, undefined, undefined);
    this.hydrateOrInitHashiState();

    this.subs.push(
      this.framework.buttonPressed.subscribe((button) => {
        if (button === Button.Check) this.onCheck();
        else if (button === Button.Reset) this.resetPuzzle();
      })
    );

    this.subs.push(
      this.store
        .select((state) => state.puzzleGameState.showErrorsImmediately)
        .subscribe((enabled: boolean) => {
          this.showErrorsImmediately = enabled;
          this.cdr.markForCheck();
        })
    );
  }

  ngOnDestroy(): void {
    for (const s of this.subs) s.unsubscribe();
    if (this.checkTimeout) clearTimeout(this.checkTimeout);
  }

  // --- Template-bound queries ---

  islandCenter(n: HashiIsland): { x: number; y: number } {
    return { x: this.boardPadding + n.x * this.cellSize, y: this.boardPadding + n.y * this.cellSize };
  }

  lineH(key: HashiBridgeKey) {
    return lineEndpointsH(key, this.islands, this.cellSize, this.boardPadding);
  }

  lineV(key: HashiBridgeKey) {
    return lineEndpointsV(key, this.islands, this.cellSize, this.boardPadding);
  }

  getOrientation(key: HashiBridgeKey): 'h' | 'v' | null {
    return getOrientation(this.islands, key);
  }

  serializeKey(key: HashiBridgeKey): string {
    return serializeKey(key);
  }

  isIslandSatisfied(n: HashiIsland): boolean {
    return currentDegree(this.bridges, n.id) >= n.requiredBridges;
  }

  isIslandOverpopulated(n: HashiIsland): boolean {
    return currentDegree(this.bridges, n.id) > n.requiredBridges;
  }

  isIslandActiveForPreview(n: HashiIsland): boolean {
    const snap = this.pointer.snapshot();
    if (snap.touchHoverIslandId === n.id) return true;
    if (snap.hoveredIslandId === n.id && !this.pointer.isSuppressingHover()) return true;
    return false;
  }

  hasCheckError(n: HashiIsland): boolean {
    return this.checkFlash && this.checkErrorNodeIds.has(n.id);
  }

  getPreviewCountFor(key: HashiBridgeKey): number {
    const sk = serializeKey(key);
    const snap = this.pointer.snapshot();
    const activeBridge = snap.dragPreviewKey ?? snap.hoveredBridgeKey;
    if (activeBridge === sk) return this.nextNonCrossingCount(key, sk);

    const activeIsland =
      snap.touchHoverIslandId ?? (snap.hoveredIslandId && !this.pointer.isSuppressingHover() ? snap.hoveredIslandId : null);
    if (activeIsland) {
      const [a, b] = sk.split('|');
      if (a === activeIsland || b === activeIsland) return this.nextNonCrossingCount(key, sk);
    }

    return 0;
  }

  mouseDownIsland(n: HashiIsland): void { this.pointer.onMouseDownIsland(n); }
  mouseUpIsland(n: HashiIsland): void { this.applyIntent(this.pointer.onMouseUpIsland(n)); }
  cycleBridge(key: HashiBridgeKey): void { this.applyIntent(this.pointer.onCycleBridgeRequested(key)); }
  enterBridge(key: HashiBridgeKey): void { this.pointer.onEnterBridge(key); this.cdr.markForCheck(); }
  leaveBridge(): void { this.pointer.onLeaveBridge(); this.cdr.markForCheck(); }
  enterIsland(n: HashiIsland): void { this.pointer.onEnterIsland(n); this.cdr.markForCheck(); }
  leaveIsland(): void { this.pointer.onLeaveIsland(); this.cdr.markForCheck(); }

  touchStartIsland(ev: TouchEvent, n: HashiIsland): void {
    if (ev.touches && ev.touches.length > 1) return;
    const t = ev.touches?.[0];
    this.pointer.onTouchStartIsland(n, t ? this.clientToSvg(t.clientX, t.clientY) : null);
    this.preventDefault(ev);
    this.cdr.markForCheck();
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(ev: MouseEvent): void {
    const svg = this.clientToSvg(ev.clientX, ev.clientY);
    this.applyIntent(this.pointer.onMouseMove(svg, this.pointerContext()));
  }

  @HostListener('document:mouseup', ['$event'])
  onDocMouseUp(ev: MouseEvent): void {
    const svg = this.clientToSvg(ev.clientX, ev.clientY);
    this.applyIntent(this.pointer.onMouseUp(svg, this.pointerContext()));
  }

  @HostListener('document:touchmove', ['$event'])
  onDocTouchMove(ev: TouchEvent): void {
    const t = ev.touches?.[0];
    if (!t) return;
    const multi = !!ev.touches && ev.touches.length > 1;
    this.preventDefault(ev);
    const svg = this.clientToSvg(t.clientX, t.clientY);
    this.applyIntent(this.pointer.onTouchMove(svg, this.pointerContext(), multi));
  }

  @HostListener('document:touchend', ['$event'])
  onDocTouchEnd(ev: TouchEvent): void {
    const t = ev.changedTouches?.[0] ?? ev.touches?.[0];
    const svg = t ? this.clientToSvg(t.clientX, t.clientY) : null;
    this.applyIntent(this.pointer.onTouchEnd(svg, this.pointerContext()));
  }

  @HostListener('document:touchcancel', ['$event'])
  onDocTouchCancel(_ev: TouchEvent): void {
    this.pointer.onTouchCancel();
    this.cdr.markForCheck();
  }

  // --- Internals ---

  private hydrateOrInitHashiState(): void {
    const stored = this.store.selectSnapshot(HashiGameState.getByID(this.puzzleID)) as HashiGameModel | null;
    if (stored && stored.bridges) {
      this.bridges = { ...this.makeEmptyBridges(), ...stored.bridges };
      this.updateProgress();
    } else {
      this.store.dispatch(new InitHashiGame({ puzzleID: this.puzzleID, bridges: { ...this.bridges } }));
    }
  }

  private persistBridges(): void {
    this.store.dispatch(new PersistHashiState({ puzzleID: this.puzzleID, bridges: { ...this.bridges } }));
  }

  private makeEmptyBridges(): HashiBridgesMap {
    const out: HashiBridgesMap = {};
    for (const k of this.bridgeKeys) out[serializeKey(k)] = 0;
    return out;
  }

  private applySizing(): void {
    const vw = typeof window !== 'undefined' ? window.innerWidth || document.documentElement.clientWidth || 0 : 0;
    const vh = typeof window !== 'undefined' ? window.innerHeight || document.documentElement.clientHeight || 0 : 0;
    const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio as number) || 1 : 1;
    this.assignSizing(computeHashiSizing(vw, vh, dpr));
    this.pointer.setCellSize(this.cellSize);
  }

  private assignSizing(s: HashiSizing): void {
    this.cellSize = s.cellSize;
    this.islandRadius = s.islandRadius;
    this.bridgeStroke = s.bridgeStroke;
    this.bridgeOffset = s.bridgeOffset;
    this.hitStrokeWidth = s.hitStrokeWidth;
    this.islandFontSize = s.islandFontSize;
    this.islandTapExtra = s.islandTapExtra;
    this.boardPadding = s.boardPadding;
  }

  private recomputeBoardDimensions(): void {
    const maxX = Math.max(...this.islands.map((n) => n.x), 0);
    const maxY = Math.max(...this.islands.map((n) => n.y), 0);
    this.widthPX = maxX * this.cellSize + this.cellSize + this.boardPadding * 2;
    this.heightPX = maxY * this.cellSize + this.cellSize + this.boardPadding * 2;
  }

  private clientToSvg(clientX: number, clientY: number): { x: number; y: number } {
    const svg: SVGSVGElement | null = this.el.nativeElement.querySelector('svg');
    if (!svg) return { x: clientX, y: clientY };
    const rect = svg.getBoundingClientRect();
    const x = (clientX - rect.left) * (this.widthPX / rect.width);
    const y = (clientY - rect.top) * (this.heightPX / rect.height);
    return { x, y };
  }

  private pointerContext() {
    return {
      islands: this.islands,
      bridges: this.bridges,
      bridgeKeys: this.bridgeKeys,
      islandCenter: (n: HashiIsland) => this.islandCenter(n),
    };
  }

  private applyIntent(intent: HashiPointerIntent): void {
    if (intent.kind === 'commit') this.tryCycleBridge(intent.key);
    this.cdr.markForCheck();
  }

  private nextNonCrossingCount(key: HashiBridgeKey, sk: string): number {
    const k = normalizeKey(this.islands, key);
    if (!k) return 0;
    const current = this.bridges[sk] ?? 0;
    const next = (current + 1) % 3;
    if (next === 0) return 0;
    if (next > current && createsCrossing(this.islands, this.bridgeKeys, this.bridges, k, next)) return 0;
    return next;
  }

  private tryCycleBridge(key: HashiBridgeKey): void {
    const k = normalizeKey(this.islands, key);
    if (!k) return;
    if (!isVisiblePair(this.islands, k)) return;

    const sk = serializeKey(k);
    const current = this.bridges[sk] ?? 0;
    const next = (current + 1) % 3;
    if (next > current && createsCrossing(this.islands, this.bridgeKeys, this.bridges, k, next)) return;

    this.bridges = { ...this.bridges, [sk]: next };
    this.store.dispatch(new IncMoveCounter({ puzzleID: this.puzzleID }));
    this.persistBridges();
    this.updateProgress();
    if (isSolved(this.islands, this.bridges)) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
    }
  }

  private updateProgress(): void {
    this.store.dispatch(
      new SetGameProgress({ puzzleID: this.puzzleID, progress: computeProgress(this.islands, this.bridges) })
    );
  }

  private resetPuzzle(): void {
    this.bridges = this.makeEmptyBridges();
    this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.HashiGame, 0);
    this.persistBridges();
    this.store.dispatch(new SetGameProgress({ puzzleID: this.puzzleID, progress: 0 }));
    this.cdr.markForCheck();
  }

  private onCheck(): void {
    if (isSolved(this.islands, this.bridges)) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
      return;
    }
    const errors = new Set<string>();
    for (const n of this.islands) {
      if (currentDegree(this.bridges, n.id) !== n.requiredBridges) errors.add(n.id);
    }
    this.checkErrorNodeIds = errors;
    this.checkFlash = true;
    if (this.checkTimeout) clearTimeout(this.checkTimeout);
    this.checkTimeout = setTimeout(() => {
      this.checkFlash = false;
      this.cdr.markForCheck();
    }, 1000);
    this.cdr.markForCheck();
  }

  private preventDefault(ev: Event): void {
    try {
      ev.preventDefault();
    } catch {
      // ignore — passive listeners on some platforms
    }
  }
}
